import { GridColumn as Column } from '@progress/kendo-react-grid';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import {
  DeleteButton,
  SecondaryButton
} from 'src/components/shared/Button/Button';
import { BooleanCell } from 'src/components/shared/DataGrid/BooleanCell';
import { BooleanFilterCell } from 'src/components/shared/DataGrid/BooleanFilterCell';
import { DataGrid } from 'src/components/shared/DataGrid/DataGrid';
import { GridCountCell } from 'src/components/shared/DataGrid/GridCountCell';
import {
  HeaderSection,
  HeaderSectionLeft,
  HeaderSectionRight
} from 'src/components/shared/HeaderSection/HeaderSection';
import { Modal } from 'src/components/shared/Modal/Modal';
import { BodyType } from 'src/components/shared/Typography/BodyType';
import { Header1 } from 'src/components/shared/Typography/Header1';
import { useIsFormDirty } from 'src/hooks/useIsFormDirty';
import { useReduxDispatch } from 'src/store';
import { isLoadingRunSystems } from 'src/store/requests/requests.selectors';
import {
  RunSystemDetailDto,
  RunSystemDto
} from 'src/store/runsystems/runsystem';
import { mapRunSystemDetailToEdit } from 'src/store/runsystems/runsystem.map';
import {
  createRunSystem,
  deleteRunSystem,
  editRunSystem,
  fetchRunSystems
} from 'src/store/runsystems/runsystems.actions';
import { RunSystemApi } from 'src/store/runsystems/runsystems.api';
import { selectRunSystems } from 'src/store/runsystems/runsystems.selectors';
import { RunSystemForm } from './RunSystemForm';

export const RunSystemsPage = () => {
  const [currentRunSystem, setCurrentRunSystem] = useState<RunSystemDto | null>(
    null
  );
  const [runSystem, setRunSystem] =
    useState<Nullable<RunSystemDetailDto>>(null);
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [showEditModal, setShowEditModal] = useState<boolean>(false);
  const [isFormDirty, callbackRef] = useIsFormDirty();

  const { t } = useTranslation();

  const dispatch = useReduxDispatch();

  const runSystems = useSelector(selectRunSystems);

  const loadingRunSystems = useSelector(isLoadingRunSystems);

  useEffect(() => {
    dispatch(fetchRunSystems());
  }, [dispatch]);

  useEffect(() => {
    if (currentRunSystem) {
      setShowDeleteModal(true);
    }
  }, [currentRunSystem]);

  const onCancel = () => {
    setCurrentRunSystem(null);
    setRunSystem(null);
    setShowDeleteModal(false);
    setShowEditModal(false);
  };

  const onCreate = () => {
    setShowEditModal(true);
  };

  const onEdit = async (runSystem: RunSystemDto) => {
    const response = await RunSystemApi.getById(runSystem.id);
    if (response.ok) {
      setRunSystem(response.data);
      setShowEditModal(true);
    }
  };

  const onDelete = (runSystem: RunSystemDto) => {
    setCurrentRunSystem(runSystem);
  };

  const onConfirmDelete = () => {
    if (currentRunSystem) {
      dispatch(deleteRunSystem(currentRunSystem.id, currentRunSystem));
      setCurrentRunSystem(null);
      setShowDeleteModal(false);
    }
  };

  const onSubmit = async (runSystem: RunSystemDetailDto) => {
    const dto = mapRunSystemDetailToEdit(runSystem);
    if (dto.id) {
      await dispatch(editRunSystem(dto.id, dto));
    } else {
      await dispatch(createRunSystem(dto));
    }

    dispatch(fetchRunSystems());
    onCancel();
  };

  return (
    <>
      <HeaderSection className="mb-32">
        <HeaderSectionLeft>
          <Header1>{t('runSystem.plural')}</Header1>
          <BodyType>{`${t('crudtext')} ${t('runSystem.plural')}`}</BodyType>
        </HeaderSectionLeft>
        <HeaderSectionRight>
          <SecondaryButton onClick={onCreate} icon="la-plus">
            {`${t('create.verb')} ${t('runSystem.singular')}`}
          </SecondaryButton>
        </HeaderSectionRight>
      </HeaderSection>

      <DataGrid
        items={runSystems}
        loading={loadingRunSystems}
        onRowClick={({ dataItem }) => onEdit(dataItem)}
        initialSort={[{ field: 'no', dir: 'asc' }]}
      >
        <Column
          field="no"
          width={200}
          title={t('props.number')}
          footerCell={() => (
            <GridCountCell
              count={runSystems?.length}
              label={t('runSystem.plural')}
            />
          )}
        />
        <Column field="name" title={t('props.name')} />
        <Column field="description" title={t('props.description')} />
        <Column
          field="runSystemArchivingName"
          title={t('runSystem.archiving')}
        />
        <Column
          width="160px"
          field="active"
          title={t('props.active')}
          cell={props => <BooleanCell {...props} />}
          filterCell={props => <BooleanFilterCell {...props} />}
        />
        <Column
          width="160px"
          field="productsRemixable"
          title={t('runSystem.productsRemixable')}
          cell={props => <BooleanCell {...props} />}
          filterCell={props => (
            <BooleanFilterCell
              trueLabel={t('props.true')}
              falseLabel={t('props.false')}
              {...props}
            />
          )}
        />
        <Column
          width="160px"
          field="mixRunSystem"
          title={t('runSystem.mixRunSystem')}
          cell={props => <BooleanCell {...props} />}
          filterCell={props => (
            <BooleanFilterCell
              trueLabel={t('props.true')}
              falseLabel={t('props.false')}
              {...props}
            />
          )}
        />
        <Column
          width="100px"
          filterable={false}
          cell={props => (
            <td>
              <DeleteButton onClick={() => onDelete(props.dataItem)} />
            </td>
          )}
        />
      </DataGrid>

      <Modal
        width={1000}
        header={`${runSystem ? t('edit') : t('create.verb')} ${t(
          'runSystem.singular'
        )}`}
        confirmText={t('save.verb')}
        onClose={() => onCancel()}
        open={showEditModal}
        allowCommonExitStrategies={!isFormDirty}
      >
        <RunSystemForm
          runSystem={runSystem}
          onCancel={onCancel}
          onSubmit={onSubmit}
          ref={callbackRef}
        />
      </Modal>

      <Modal
        width={500}
        header={`${t('delete.verb')} ${t('runSystem.singular')}`}
        confirmText={t('delete.verb')}
        onClose={() => onCancel()}
        onConfirm={() => onConfirmDelete()}
        open={showDeleteModal}
        showActions
      >
        <BodyType>
          {t('runSystem.delete')} <q>{currentRunSystem?.name}</q>?
        </BodyType>
      </Modal>
    </>
  );
};
